/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function loadingView() {
  const loadingElement = document.querySelector('#loadingView');
  if (!loadingElement) {
    return;
  }

  // アクセス元を判定（サイト内回遊かどうか）
  const isInternalNavigation =
    document.referrer &&
    (document.referrer.includes(window.location.hostname) ||
      document.referrer.includes(window.location.host));

  const hideImmediately = () => {
    document.documentElement.classList.remove('lock-all');
    document.documentElement.classList.remove('loading-uninitialize');
    loadingElement.classList.add('c-hidden');
  };

  // サイト内回遊でTOPに戻ってきた場合は即時非表示
  if (isInternalNavigation) {
    hideImmediately();
    return;
  }

  // 一度表示したら、テーマ設定「一度表示したら再表示しない時間」の間は同じブラウザで再表示しない
  // （0 のときは毎回表示。localStorage が使えないブラウザでも毎回表示）
  const SHOWN_AT_KEY = 'loadingViewShownAt';
  const repeatHours = Number(loadingElement.dataset.repeatHours) || 0;

  if (repeatHours > 0) {
    let shownAt = 0;

    try {
      shownAt = Number(window.localStorage.getItem(SHOWN_AT_KEY)) || 0;
    } catch (error) {
      shownAt = 0;
    }

    if (Date.now() - shownAt < repeatHours * 60 * 60 * 1000) {
      hideImmediately();
      return;
    }

    try {
      window.localStorage.setItem(SHOWN_AT_KEY, String(Date.now()));
    } catch (error) {
      // 保存できないときはそのまま表示する
    }
  }

  const loadingVideo = loadingElement.querySelector('video[data-mobile-src][data-desktop-src]');
  if (loadingVideo) {
    const isMobile = window.matchMedia('(max-width: 740px)').matches;
    const videoSource = isMobile ? loadingVideo.dataset.mobileSrc : loadingVideo.dataset.desktopSrc;

    if (videoSource) {
      loadingVideo.src = videoSource;
      loadingVideo.load();
    }
  }

  document.documentElement.classList.remove('lock-all');
  document.documentElement.classList.remove('loading-uninitialize');

  const hideLoadingView = () => {
    const handleTransitionEnd = () => {
      loadingElement.classList.add('c-hidden');
      loadingElement.removeEventListener('transitionend', handleTransitionEnd);
    };

    loadingElement.addEventListener('transitionend', handleTransitionEnd);

    // 5秒表示してからフェード開始
    window.setTimeout(() => {
      loadingElement.style.opacity = '0';
    }, 3600);

    // transitionendが発火しなかった場合の保険
    window.setTimeout(() => {
      if (!loadingElement.classList.contains('c-hidden')) {
        loadingElement.classList.add('c-hidden');
      }
    }, 5500);
  };

  window.addEventListener('load', hideLoadingView, { once: true });
}

function toggleSearchButton() {
  const searchButtons = document.querySelectorAll('.c-search-button');

  searchButtons.forEach((searchButton) => {
    searchButton.addEventListener('click', () => {
      const controlledId = searchButton.getAttribute('aria-controls');
      const controlledElement = document.getElementById(controlledId);
      const isOpen = controlledElement.classList.contains('is-open');

      if (isOpen) {
        controlledElement.classList.remove('is-open');
      } else {
        controlledElement.classList.add('is-open');
      }
    });
  });
}

window.addEventListener('DOMContentLoaded', function () {
  loadingView();
  toggleSearchButton();
});
